import * as React from 'react';
import styled, { useTheme } from "styled-components";
import { MapEvent, MapMouseEvent, Map as OriginalMap } from 'mapbox-gl';
import { Map as GLMap, Layer, Source, ViewState, ViewStateChangeEvent, useMap as useGLMap } from 'react-map-gl/mapbox';
import { ViewStateContext, MapInstanceContext, IMapInstance } from '@longline/aqua-ui/map/context';

import { MapboxDem } from '../services/MapboxDem';

interface IMapProps {
  /** @ignore */
  children?: React.ReactNode;
  /**
   * Map access token.
   */
  token: string;
  /**
   * MapStudio style.
   */
  style: string;
  /**
   * Initial longitude.
   * @default -64.6
   */
  longitude?: number;
  /**
   * Initial latitude.
   * @default 44.02
   */
  latitude?: number;
  /**
   * Initial zoom.
   * @default 11
   */
  zoom?: number;
  /**
   * Initial pitch.
   * @default 0
   */
  pitch?: number;
  /**
   * Initial bearing.
   * @default 0
   */
  bearing?: number;
  /**
   * Initial padding.
   * @default 0,0,0,0
   */
  padding?: {
    readonly top?: number;
	  readonly bottom?: number;
	  readonly right?: number;
	  readonly left?: number;
  }
  /**
   * If set to a number, satellite imagery is clipped at that elevation threshold.
   * Pixels with elevation below the threshold become transparent.
   * A value of `1` generally works for ocean clipping.
   * Set to `false` or `undefined` to disable clipping.
   */
  clipped?: number | false;
  /**
   * Optional Mapbox logo position.
   * @default top-right
   */
  logoPosition?: "top-right" | "top-left" | "bottom-left" | "bottom-right";
  /**
   * Optional minimum zoom level.
   * @default 1
   */
  minZoom?: number;
  /**
   * Optional maximum zoom level.
   * @default 20
   */
  maxZoom?: number;
  /**
   * IDs of interactive layers.
   * @default []
   */
  interactiveLayerIds?: string[];
  /**
   * If set, map cannot be rotated using dragging or touch gestures.
   * @default false
   */
  noRotate?: boolean;
  /**
   * If set, `preserveDrawingBuffer` is passed on to mapbox. This is necessary
   * when you want to capture the content of the mapbox canvas.
   * @default false
   */
  preserveDrawingBuffer?: boolean;
  /**
   * Symbol fade duration (ms)
   * @default 300
   */
  fadeDuration?: number;
  /**
   * Map zoomes when double-clicked.
   * @default true
   */
  doubleClickZoom?: boolean;
  /**
   * Map cursor
   */
  cursor?: string;
  /**
   * Fired when map is loaded.
   */
  onLoad?: (map: OriginalMap) => void;
  /**
   * Fired when map moves.
   */
  onMove?: (e: ViewStateChangeEvent) => void;
  /**
   * Fired when map stops moving.
   */
  onMoveEnd?: (e: ViewStateChangeEvent) => void;
  /**
   * Fired when map is clicked.
   */
  onClick?: (e: MapMouseEvent) => void;
  /**
   * Fired when map is double-clicked.
   */
  onDblClick?: (e: MapMouseEvent) => void;
  /**
   * Fired when mouse moves over map.
   */
  onMouseMove?: (e: MapMouseEvent) => void;
  /**
   * Fired when mouse leaves map.
   */
  onMouseLeave?: (e: MapMouseEvent) => void;
}

const useViewState = () => {
  return React.useContext(ViewStateContext);
}

/**
 * Load an image from a URL into an Image element.
 */
const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Create a 2D canvas context of the specified size.
 */
const createCanvasContext = (width = 512, height = 512): CanvasRenderingContext2D => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas.getContext('2d')!;
}

/**
 * Load an image URL into a canvas and return the context.
 */
const loadImageToCanvas = async (url: string): Promise<CanvasRenderingContext2D> => {
  const img = await loadImage(url);
  const bmp = await createImageBitmap(img);
  const ctx = createCanvasContext(512, 512);
  ctx.drawImage(bmp, 0, 0);
  return ctx;
}

/**
 * Encode a CanvasRenderingContext2D as a PNG Response.
 */
const canvasToResponse = async (ctx: CanvasRenderingContext2D): Promise<Response> => {
  const blob = await new Promise<Blob>((resolve) =>
    ctx.canvas.toBlob((b) => resolve(b!))
  );
  return new Response(blob);
}

/**
 * Process a satellite tile by making water pixels transparent.
 * Water is determined by checking a DEM tile from MapboxDem — pixels with
 * elevation below the threshold are considered water.
 *
 * MapboxDem handles parent-tile fallback, so high zoom (above
 * terrain-dem-v1's z=14 cap) gets a coarser-resolution but still correct
 * clipping mask. If no DEM data is available at any zoom (shouldn't happen
 * once the user has interacted with the map and a sku has been captured)
 * we fall back to "all water", letting the base satellite layer show
 * through.
 *
 * @param satelliteUrl - URL to fetch the satellite tile
 * @param z, x, y - Tile coordinates of the satellite tile being processed
 * @param threshold - Elevation threshold in meters. Pixels below this are made transparent.
 * @returns Response containing the processed satellite image
 */
const processClippedSatelliteTile = async (
  satelliteUrl: string,
  z: number,
  x: number,
  y: number,
  threshold: number,
): Promise<Response> => {
  const [demSubTile, ctxSatellite] = await Promise.all([
    MapboxDem.getTile(z, x, y),
    loadImageToCanvas(satelliteUrl),
  ]);

  if (!demSubTile) {
    // No DEM data anywhere up the parent chain — treat the entire tile
    // as water and let the base satellite layer show through.
    ctxSatellite.clearRect(0, 0, 512, 512);
    return canvasToResponse(ctxSatellite);
  }

  // Render the (possibly sub-region of a parent) DEM bitmap at our
  // tile's full 512×512 resolution so we can read elevation per
  // satellite pixel.
  const ctxDem = createCanvasContext(512, 512);
  ctxDem.imageSmoothingEnabled = false; // nearest-neighbour: we want raw RGB samples, not interpolated colors
  ctxDem.drawImage(
    demSubTile.bitmap,
    demSubTile.srcX, demSubTile.srcY, demSubTile.srcSize, demSubTile.srcSize,
    0, 0, 512, 512,
  );

  const satelliteData = ctxSatellite.getImageData(0, 0, 512, 512);
  const demData = ctxDem.getImageData(0, 0, 512, 512);

  // Process each pixel
  for (let i = 0; i < 512 * 512 * 4; i += 4) {
    const elevation = MapboxDem.decodeElevation(
      demData.data[i], demData.data[i + 1], demData.data[i + 2],
    );

    // Make pixel transparent if below threshold (water)
    if (elevation < threshold) {
      satelliteData.data[i] = 0;     // R
      satelliteData.data[i + 1] = 0; // G
      satelliteData.data[i + 2] = 0; // B
      satelliteData.data[i + 3] = 0; // A (transparent)
    }
  }

  ctxSatellite.putImageData(satelliteData, 0, 0);
  return canvasToResponse(ctxSatellite);
}

/**
 * `Map` creates a react-mapbox-gl map instance. The map stretches to fill its
 * container.
 *
 * The map's current ViewState is tracked in local state, and fed into a context provider.
 * The ViewState can be retrieved by child components using `useViewState()`.
 */
const MapBase = (props: IMapProps) => {
  const theme = useTheme();
  const [viewState, setViewState] = React.useState<ViewState>({
    // Defaults are applied by the LocalMap wrapper via destructuring, so by
    // the time MapBase sees props they're populated; the `??` here just
    // reassures TS since IMapProps types them as optional for the public API.
    longitude: props.longitude ?? 0,
    latitude: props.latitude ?? 0,
    zoom: props.zoom ?? 11,
    pitch: props.pitch ?? 0,
    bearing: props.bearing ?? 0,
    padding: props.padding || { top: 0, bottom: 0, left: 0, right: 0 }
  });

  // Store the clipping threshold in a ref so the fetch interceptor always has
  // access to the current value without needing to be re-created.
  const clippedRef = React.useRef(props.clipped);
  React.useEffect(() => {
    clippedRef.current = props.clipped;
  }, [props.clipped]);

  // Make the access token available to the shared MapboxDem service so
  // off-map callers (e.g. DigitalElevationModel) can reach it without
  // needing the LocalMap-only fetch interceptor to fire first.
  React.useEffect(() => {
    MapboxDem.setAccessToken(props.token);
  }, [props.token]);

  // Set up fetch interceptor to process satellite tiles
  React.useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      // Extract URL string from input (could be string, URL, or Request)
      let url: string;
      if (typeof input === 'string') {
        url = input;
      } else if (input instanceof URL) {
        url = input.toString();
      } else if (input instanceof Request) {
        url = input.url;
      } else {
        return originalFetch(input, init);
      }

      // Capture the sku token from any passing Mapbox URL so off-map
      // consumers (DigitalElevationModel) can fetch DEM tiles too.
      // Mapbox GL JS appends ?sku=… to every api.mapbox.com tile request.
      MapboxDem.captureSkuFromUrl(url);

      // Match our overlay's tile requests by their fake dataset name.
      //
      // We can't disambiguate by URL ratio (e.g. @2x): on high-DPR displays
      // (devicePixelRatio >= 2) Mapbox GL JS rewrites the base style's
      // satellite tile URLs to /v4/mapbox.satellite/.../@2x.webp too, which
      // would collide with the overlay's URL pattern and cause us to clip
      // water out of the base layer (showing the dark style background as
      // "black water").
      //
      // Mapbox doesn't strip the dataset segment of the path, so using a
      // fake dataset name (mapbox.satellite-clipped) gives us a URL pattern
      // that the base layer will never produce. Inside the handler we
      // translate it back to the real mapbox.satellite tile to fetch.
      const satelliteMatch = url.match(
        /^https:\/\/api\.mapbox\.com\/v4\/mapbox\.satellite-clipped\/(\d+)\/(\d+)\/(\d+)@2x\.webp\?(.+)$/
      );

      if (satelliteMatch && typeof clippedRef.current === 'number') {
        const [, zStr, xStr, yStr, queryString] = satelliteMatch;
        const z = parseInt(zStr);
        const x = parseInt(xStr);
        const y = parseInt(yStr);
        const threshold = clippedRef.current;

        // Translate the fake dataset name back to the real satellite tile.
        // <img>-based satellite load bypasses the interceptor (no recursion),
        // and the DEM fetch goes through MapboxDem which uses the captured
        // sku.
        const satelliteUrl = `https://api.mapbox.com/v4/mapbox.satellite/${z}/${x}/${y}@2x.webp?${queryString}`;
        return processClippedSatelliteTile(satelliteUrl, z, x, y, threshold);
      }

      // For all other requests, use original fetch
      return originalFetch(input, init);
    };

    // Cleanup: restore original fetch on unmount
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const handleLoad = (e: MapEvent) => {
    const map = e.target;
    if (props.noRotate) {
      map.dragRotate.disable();
      map.touchZoomRotate.disableRotation();
    }
    if (props.onLoad) props.onLoad(map);
  }

  const handleMove = (e: ViewStateChangeEvent) => {
    setViewState(e.viewState);
    if (props.onMove) props.onMove(e);
  }

  // Determine if clipping is enabled (must be a number, not false/undefined)
  const clippingEnabled = typeof props.clipped === 'number';

  return (
    <GLMap
      {...viewState}
      mapboxAccessToken={props.token}
      style={{ width: '100%', height: '100%', minHeight: '300px', font: theme.font.bodyMedium }}
      logoPosition={props.logoPosition}
      mapStyle={props.style}
      minZoom={props.minZoom}
      maxZoom={props.maxZoom}
      interactiveLayerIds={props.interactiveLayerIds}
      doubleClickZoom={props.doubleClickZoom}
      cursor={props.cursor}
      preserveDrawingBuffer={props.preserveDrawingBuffer}
      fadeDuration={props.fadeDuration}
      onMove={handleMove}
      onMoveEnd={props.onMoveEnd}
      onMouseMove={props.onMouseMove}
      onMouseLeave={props.onMouseLeave}
      onLoad={handleLoad}
      onClick={props.onClick}
      onDblClick={props.onDblClick}
    >
      {/*
        Satellite overlay layer with water clipping.

        The "mapbox.satellite-clipped" segment is a fake dataset name — no
        such dataset exists on Mapbox. The window.fetch interceptor in
        MapBase recognises this URL pattern, fetches the real
        mapbox.satellite tile and a mapbox.terrain-rgb tile, and returns a
        composited result with water pixels made transparent. Using a fake
        dataset name (rather than relying on @2x as a discriminator) is
        what keeps us from clipping the base style's satellite tiles by
        accident on high-DPR displays.

        The key includes props.clipped so changing the threshold forces
        Mapbox to re-fetch tiles instead of serving the previous threshold's
        cached output.

        When clipping is disabled, this Source is not rendered, and the base
        map style's satellite layer shows through normally.
      */}
      {clippingEnabled && (
        <Source
          key={`satellite-clipped-${props.clipped}`}
          type="raster"
          tiles={[
            `https://api.mapbox.com/v4/mapbox.satellite-clipped/{z}/{x}/{y}@2x.webp?access_token=${props.token}`
          ]}
          tileSize={512}
        >
          {/*
            Insert just before the first label (symbol) layer in the style
            so labels stay above the clipped satellite. Aqua-ui's
            InterpolationLayer / ParticlesLayer use beforeId="overlay" to
            slot themselves just below this layer, giving the final order:
            satellite → roads/admin → custom OpenGL → overlay → labels.
            "path-pedestrian-label" is the first symbol layer in
            static/mapbox-style.json (layer index 27); update if the style
            is ever swapped for one whose first symbol layer differs.
          */}
          <Layer id="overlay" type="raster" beforeId="path-pedestrian-label" />
        </Source>
      )}

      <ViewStateContext.Provider value={{ ...viewState }}>
        <MapInstanceBridge>
          <Controls>
            {props.children}
          </Controls>
        </MapInstanceBridge>
      </ViewStateContext.Provider>
    </GLMap>
  );
}

/**
 * Publishes the map instance on AquaUI's own context.
 *
 * Since AquaUI 1.0.353 the map controls no longer read react-map-gl's useMap()
 * -- importing one zoom button used to drag in the whole of mapbox-gl -- so a
 * host driving its own map has to hand the instance over itself. Without this,
 * every control calling useMap() gets null: ZoomInButton asserts non-null and
 * dies on getMaxZoom().
 *
 * Must render inside GLMap, which is what puts react-map-gl's context in scope
 * for the hook. Mirrors AquaUI's own MapInstanceBridge in map/Map.
 */
const MapInstanceBridge = ({ children }: { children?: React.ReactNode }) => {
  const { current } = useGLMap();
  return (
    <MapInstanceContext.Provider value={(current as unknown as IMapInstance) ?? null}>
      {children}
    </MapInstanceContext.Provider>
  );
}

const Controls = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  & > * {
    pointer-events: all;
  }

  ${p => p.theme.font.bodyMedium};
`

const LocalMap = ({
  longitude = -64.6,
  latitude = 44.02,
  zoom = 11,
  pitch = 0,
  bearing = 0,
  logoPosition = 'top-right',
  interactiveLayerIds = [],
  noRotate = false,
  preserveDrawingBuffer = false,
  minZoom = 1,
  maxZoom = 20,
  fadeDuration = 300,
  doubleClickZoom = true,
  ...props
}: IMapProps) => (
  <MapBase
    longitude={longitude}
    latitude={latitude}
    zoom={zoom}
    pitch={pitch}
    bearing={bearing}
    logoPosition={logoPosition}
    interactiveLayerIds={interactiveLayerIds}
    noRotate={noRotate}
    preserveDrawingBuffer={preserveDrawingBuffer}
    minZoom={minZoom}
    maxZoom={maxZoom}
    fadeDuration={fadeDuration}
    doubleClickZoom={doubleClickZoom}
    {...props}
  />
);

export { LocalMap, IMapProps, useViewState }
